
  (() => {
    const compactFilters = window.matchMedia('(max-width: 1100px)');
    const mobileBrowsePaths = window.matchMedia('(max-width: 700px)');
    const listFacets = ['rooms', 'subjects', 'palette', 'styleMood'];
    const allFacets = [...listFacets, 'livingArt'];

    const parseList = (value) => {
      try {
        const parsed = JSON.parse(value || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    };

    const initDiscovery = (section) => {
      if (!section || section.dataset.dapDiscoveryBound === 'true') return;
      section.dataset.dapDiscoveryBound = 'true';

      const browsePaths = section.querySelector('[data-dap-browse-paths]');
      if (browsePaths) {
        let desktopBrowseOpen = browsePaths.open;
        const syncBrowsePaths = () => { browsePaths.open = mobileBrowsePaths.matches || desktopBrowseOpen; };
        browsePaths.addEventListener('toggle', () => {
          if (!mobileBrowsePaths.matches) desktopBrowseOpen = browsePaths.open;
        });
        mobileBrowsePaths.addEventListener?.('change', syncBrowsePaths);
        syncBrowsePaths();
      }

      const shell = section.querySelector('[data-dap-filter-shell]');
      const form = section.querySelector('[data-dap-discovery-form]');
      const filterDialog = section.querySelector('[data-dap-filter-dialog]');
      const filterTrigger = shell?.querySelector(':scope > summary');
      const grid = section.querySelector('[data-dap-results-grid]');
      let cards = [];
      const inputs = [...section.querySelectorAll('[data-dap-discovery-form] input[type="checkbox"]')];
      const sort = section.querySelector('[data-dap-sort]');
      const resultCount = section.querySelector('[data-dap-results-count]');
      const activeFilters = section.querySelector('[data-dap-active-filters]');
      const chips = section.querySelector('[data-dap-filter-chips]');
      const activeCount = section.querySelector('[data-dap-active-count]');
      const showButton = section.querySelector('[data-dap-filter-show]');
      const loadMore = section.querySelector('[data-dap-load-more]');
      const paginationStatus = section.querySelector('[data-dap-pagination-status]');
      const paginationMessage = section.querySelector('[data-dap-pagination-message]');
      const paginationRetry = section.querySelector('[data-dap-pagination-retry]');
      const paginationLinks = section.querySelector('[data-dap-pagination-links]');
      const emptyState = section.querySelector('[data-dap-empty-state]');
      const pageSize = Number(section.dataset.pageSize) || 12;
      let visibleLimit = pageSize;
      let cardData = new Map();
      let inertedBackground = [];

      const filterFocusable = () => [...filterDialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.getClientRects().length > 0 && !element.closest('[inert]'));

      const releaseFilterBackground = () => {
        inertedBackground.forEach((element) => element.removeAttribute('inert'));
        inertedBackground = [];
      };

      const isolateFilterBackground = () => {
        releaseFilterBackground();
        let branch = filterDialog;
        while (branch && branch !== document.body) {
          const parent = branch.parentElement;
          if (!parent) break;
          [...parent.children].forEach((sibling) => {
            if (sibling === branch || sibling.matches('script, style, link, template') || sibling.hasAttribute('inert')) return;
            sibling.setAttribute('inert', '');
            inertedBackground.push(sibling);
          });
          branch = parent;
        }
      };

      const deactivateFilterDialog = (returnFocus = false) => {
        filterDialog.removeAttribute('role');
        filterDialog.removeAttribute('aria-modal');
        releaseFilterBackground();
        document.body.classList.remove('dap-filter-open');
        if (returnFocus) requestAnimationFrame(() => filterTrigger?.focus());
      };

      const activateFilterDialog = () => {
        filterDialog.setAttribute('role', 'dialog');
        filterDialog.setAttribute('aria-modal', 'true');
        isolateFilterBackground();
        document.body.classList.add('dap-filter-open');
        requestAnimationFrame(() => {
          const preferredTarget = filterDialog.querySelector('[data-dap-filter-close]') || filterFocusable()[0] || filterDialog;
          preferredTarget.focus();
        });
      };

      const closeFilterDialog = (returnFocus = true) => {
        shell.removeAttribute('open');
        deactivateFilterDialog(returnFocus);
      };

      const registerCards = () => {
        cards = [...section.querySelectorAll('[data-dap-filter-item]')];
        cardData = new Map(cards.map((card) => [card, {
          rooms: parseList(card.dataset.rooms),
          subjects: parseList(card.dataset.subjects),
          palette: parseList(card.dataset.palette),
          styleMood: parseList(card.dataset.styleMood),
          livingArt: card.dataset.livingArt === 'true',
          price: Number(card.dataset.price) || 0,
          published: Number(card.dataset.published) || 0,
          title: card.dataset.title || '',
          curatorRank: Number.isFinite(Number(card.dataset.curatorRank)) ? Number(card.dataset.curatorRank) : 999,
          sourceOrder: Number(card.dataset.dapSourceOrder) || 0
        }]));
      };

      const selectedFor = (facet) => inputs.filter((input) => input.name === facet && input.checked).map((input) => input.value);

      const matchesValue = (data, facet, value) => {
        if (listFacets.includes(facet)) return data[facet].includes(value);
        if (facet === 'livingArt') return data.livingArt;
        return true;
      };

      const matchesCard = (card, ignoredFacet = '') => {
        const data = cardData.get(card);
        return allFacets.every((facet) => {
          if (facet === ignoredFacet) return true;
          const selected = selectedFor(facet);
          return selected.length === 0 || selected.some((value) => matchesValue(data, facet, value));
        });
      };

      const sortedCards = () => [...cards].sort((first, second) => {
        const a = cardData.get(first);
        const b = cardData.get(second);
        const sourceOrder = a.sourceOrder - b.sourceOrder;
        if (sort.value === 'newest') return b.published - a.published || sourceOrder;
        if (sort.value === 'price-low') return a.price - b.price || a.curatorRank - b.curatorRank || sourceOrder;
        if (sort.value === 'price-high') return b.price - a.price || a.curatorRank - b.curatorRank || sourceOrder;
        if (sort.value === 'title') return a.title.localeCompare(b.title) || sourceOrder;
        return a.curatorRank - b.curatorRank || sourceOrder;
      });

      const labelFromTemplate = (element, count) => {
        const template = count === 1 ? element.dataset.one : element.dataset.many;
        return (template || '').replace('[count]', String(count));
      };

      const updateOptionCounts = () => {
        inputs.forEach((input) => {
          const count = cards.filter((card) => matchesCard(card, input.name) && matchesValue(cardData.get(card), input.name, input.value)).length;
          const label = input.closest('label');
          const countNode = label?.querySelector('[data-dap-option-count]');
          if (countNode) countNode.textContent = String(count);
          input.disabled = count === 0 && !input.checked;
          if (Number(section.dataset.underlyingCount) <= 3 && label) label.hidden = count === 0 && !input.checked;
        });
      };

      const updateChips = () => {
        const selectedInputs = inputs.filter((input) => input.checked);
        const hasFilters = selectedInputs.length > 0;
        activeFilters.hidden = !hasFilters;
        activeCount.hidden = !hasFilters;
        activeCount.textContent = String(selectedInputs.length);
        section.querySelectorAll('[data-dap-clear-all]').forEach((button) => { button.hidden = !hasFilters; });
        chips.replaceChildren(...selectedInputs.map((input) => {
          const button = document.createElement('button');
          const label = input.closest('label')?.querySelector('[data-dap-option-label]')?.textContent?.trim() || input.value;
          button.type = 'button';
          button.dataset.dapRemoveFilter = input.name;
          button.dataset.value = input.value;
          button.setAttribute('aria-label', activeFilters.dataset.removeTemplate.replace('[label]', label));
          button.append(document.createTextNode(`${label} `));
          const close = document.createElement('span');
          close.setAttribute('aria-hidden', 'true');
          close.textContent = '×';
          button.append(close);
          return button;
        }));
      };

      const syncUrl = () => {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        url.searchParams.delete('sort_by');
        allFacets.forEach((facet) => url.searchParams.delete(`dap_${facet}`));
        inputs.filter((input) => input.checked).forEach((input) => url.searchParams.append(`dap_${input.name}`, input.value));
        if (sort.value === 'curator') url.searchParams.delete('dap_sort');
        else url.searchParams.set('dap_sort', sort.value);
        const next = `${url.pathname}${url.search}${url.hash}`;
        if (next !== `${location.pathname}${location.search}${location.hash}`) history.pushState({}, '', next);
      };

      const render = (updateUrl = true) => {
        section.dispatchEvent(new CustomEvent('dap:collection:will-render', { bubbles: true }));
        const ordered = sortedCards();
        ordered.forEach((card) => grid.append(card));
        const matching = ordered.filter((card) => matchesCard(card));
        cards.forEach((card) => { card.hidden = true; card.removeAttribute('data-dap-exhibition-slot'); });
        const visible = matching.slice(0, visibleLimit);
        visible.forEach((card, index) => {
          card.hidden = false;
          card.dataset.dapExhibitionSlot = String(index % 12 + 1);
        });
        const count = matching.length;
        resultCount.textContent = labelFromTemplate(resultCount, count);
        showButton.textContent = labelFromTemplate(showButton, count);
        emptyState.hidden = count !== 0;
        const remaining = Math.max(0, count - visibleLimit);
        loadMore.hidden = remaining === 0;
        if (remaining > 0) loadMore.textContent = labelFromTemplate(loadMore, Math.min(pageSize, remaining));
        updateOptionCounts();
        updateChips();
        const smallCollection = Number(section.dataset.underlyingCount) <= 3;
        const hideShell = smallCollection && !inputs.some((input) => input.checked);
        if (hideShell && shell.open && compactFilters.matches) {
          closeFilterDialog(false);
          requestAnimationFrame(() => sort.focus());
        }
        shell.hidden = hideShell;
        if (updateUrl) syncUrl();
        section.dataset.dapCollectionRendered = 'true';
        section.dispatchEvent(new CustomEvent('dap:collection:rendered', {
          bubbles: true,
          detail: { count, visibleCount: visible.length, sort: sort.value }
        }));
      };

      const readUrl = () => {
        const params = new URLSearchParams(window.location.search);
        allFacets.forEach((facet) => {
          const requestedValue = params.get(`dap_${facet}`);
          inputs.filter((input) => input.name === facet).forEach((input) => { input.checked = input.value === requestedValue; });
        });
        sort.value = 'curator';
        let requestedSort = params.get('dap_sort');
        if (!requestedSort && params.get('sort_by') === 'created-descending') requestedSort = 'newest';
        if (requestedSort && [...sort.options].some((option) => option.value === requestedSort)) sort.value = requestedSort;
      };

      const loadPageChain = async (startUrl, direction) => {
        const loadedCards = [];
        const visited = new Set();
        let pageUrl = startUrl;
        try {
          while (pageUrl) {
            const resolvedUrl = new URL(pageUrl, window.location.href).href;
            if (visited.has(resolvedUrl)) throw new Error('Repeated gallery page');
            visited.add(resolvedUrl);
            const response = await fetch(resolvedUrl, { headers: { Accept: 'text/html' } });
            if (!response.ok) throw new Error('Gallery page unavailable');
            const page = new DOMParser().parseFromString(await response.text(), 'text/html');
            const pageSection = page.querySelector(`[data-dap-collection-discovery][data-section-id="${section.dataset.sectionId}"]`);
            const pageGrid = pageSection?.querySelector('[data-dap-results-grid]');
            if (!pageGrid) throw new Error('Gallery page missing');
            loadedCards.push(...pageGrid.querySelectorAll('[data-dap-filter-item]'));
            pageUrl = pageGrid.dataset[direction] || '';
          }
          return { cards: loadedCards, complete: true };
        } catch (error) {
          return { cards: loadedCards, complete: false };
        }
      };

      const loadRemainingPages = async () => {
        const previousUrl = grid.dataset.previousUrl;
        const nextUrl = grid.dataset.nextUrl;
        if ((!previousUrl && !nextUrl) || section.dataset.dapPaginationState === 'loading') return;
        section.dataset.dapPaginationState = 'loading';
        section.setAttribute('aria-busy', 'true');
        if (paginationStatus) paginationStatus.hidden = false;
        if (paginationMessage) paginationMessage.textContent = paginationMessage.dataset.loading;
        if (paginationRetry) paginationRetry.disabled = true;
        if (paginationLinks) paginationLinks.hidden = true;
        let complete = false;
        try {
          const results = await Promise.all([
            loadPageChain(previousUrl, 'previousUrl'),
            loadPageChain(nextUrl, 'nextUrl')
          ]);
          if (!section.isConnected) return;
          const pageCards = results.flatMap((result) => result.cards);
          const productIds = new Set(cards.map((card) => card.dataset.productId));
          pageCards.forEach((card) => {
            if (productIds.has(card.dataset.productId)) return;
            productIds.add(card.dataset.productId);
            grid.append(card);
          });
          registerCards();
          render(false);
          section.dispatchEvent(new CustomEvent('dap:collection:updated', { bubbles: true }));
          complete = results.every((result) => result.complete);
        } catch (error) {
          complete = false;
        } finally {
          const returnPaginationFocus = complete && document.activeElement === paginationRetry;
          section.dataset.dapPaginationState = complete ? 'complete' : 'error';
          if (paginationStatus) paginationStatus.hidden = complete;
          if (paginationMessage && !complete) paginationMessage.textContent = paginationMessage.dataset.error;
          if (paginationRetry) {
            paginationRetry.disabled = false;
            paginationRetry.hidden = complete;
          }
          if (returnPaginationFocus) resultCount.focus({ preventScroll: true });
          if (paginationLinks) paginationLinks.hidden = complete;
          section.removeAttribute('aria-busy');
        }
      };

      const syncShell = () => {
        deactivateFilterDialog(false);
        if (compactFilters.matches) shell.removeAttribute('open');
        else shell.setAttribute('open', '');
      };

      form.addEventListener('change', (event) => {
        const changedInput = event.target;
        if (changedInput.matches?.('input[type="checkbox"]') && changedInput.checked) {
          inputs.filter((input) => input !== changedInput && input.name === changedInput.name).forEach((input) => { input.checked = false; });
        }
        visibleLimit = pageSize;
        render();
      });
      sort.addEventListener('change', () => {
        visibleLimit = pageSize;
        render();
      });
      section.addEventListener('click', (event) => {
        if (event.target.closest('[data-dap-pagination-retry]')) {
          loadRemainingPages();
          return;
        }
        const remove = event.target.closest('[data-dap-remove-filter]');
        if (remove) {
          const chipIndex = [...chips.children].indexOf(remove);
          const input = inputs.find((candidate) => candidate.name === remove.dataset.dapRemoveFilter && candidate.value === remove.dataset.value);
          if (input) input.checked = false;
          visibleLimit = pageSize;
          render();
          (chips.children[chipIndex] || chips.lastElementChild || sort).focus({ preventScroll: true });
          return;
        }
        if (event.target.closest('[data-dap-clear-all]')) {
          inputs.forEach((input) => { input.checked = false; });
          visibleLimit = pageSize;
          render();
          const focusTarget = compactFilters.matches && shell.open ? filterDialog.querySelector('[data-dap-filter-close]') : sort;
          focusTarget?.focus({ preventScroll: true });
          return;
        }
        if (event.target.closest('[data-dap-filter-close], [data-dap-filter-show]')) {
          closeFilterDialog(true);
          return;
        }
        if (event.target.closest('[data-dap-load-more]')) {
          visibleLimit += pageSize;
          render(false);
        }
      });
      shell.addEventListener('toggle', () => {
        if (compactFilters.matches && shell.open) activateFilterDialog();
        else deactivateFilterDialog(false);
      });
      section.addEventListener('keydown', (event) => {
        if (!compactFilters.matches || !shell.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeFilterDialog(true);
          return;
        }
        if (event.key !== 'Tab') return;
        const focusable = filterFocusable();
        if (focusable.length === 0) {
          event.preventDefault();
          filterDialog.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
      section.addEventListener('dap:filter-breakpoint-change', syncShell);

      window.addEventListener('popstate', () => { readUrl(); visibleLimit = pageSize; render(false); });
      registerCards();
      readUrl();
      syncShell();
      render(false);
      loadRemainingPages();
    };

    document.querySelectorAll('[data-dap-collection-discovery]').forEach(initDiscovery);
    compactFilters.addEventListener?.('change', () => document.querySelectorAll('[data-dap-collection-discovery]').forEach((section) => {
      section.dispatchEvent(new CustomEvent('dap:filter-breakpoint-change'));
    }));
    document.addEventListener('shopify:section:load', (event) => initDiscovery(event.target.querySelector('[data-dap-collection-discovery]')));
  })();

(() => {
  'use strict';

  if (window.dapGalleryExhibitionBound) return;
  window.dapGalleryExhibitionBound = true;

  const sectionSelector = '[data-dap-collection-discovery]';
  const itemSelector = '[data-dap-filter-item]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  const nearViewport = (rect) => rect.bottom > -160 && rect.top < window.innerHeight + 160;

  const init = (section) => {
    if (!section || !section.matches?.(sectionSelector)) return null;
    if (instances.has(section)) return instances.get(section);

    const grid = section.querySelector('[data-dap-results-grid]');
    if (!grid) return null;
    const animations = new Map();
    let before = new Map();
    let frame = 0;
    let generation = 0;
    let hasRendered = section.dataset.dapCollectionRendered === 'true';
    let pendingRender = false;

    const visibleCards = () => [...grid.querySelectorAll(itemSelector)].filter((card) => !card.hidden);

    const cancelMotion = () => {
      generation += 1;
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      animations.forEach((animation, card) => {
        animation.cancel();
        card.removeAttribute('data-dap-gallery-moving');
      });
      animations.clear();
    };


    const assignPositions = () => {
      visibleCards().forEach((card, index) => { card.dataset.dapExhibitionSlot = String(index % 12 + 1); });
    };

    const beforeRender = () => {
      // Read an in-flight position before cancelling it, so a rapid next action
      // begins exactly where the visitor currently sees the existing card.
      before = hasRendered && !reducedMotion.matches
        ? new Map(visibleCards().map((card) => [card, card.getBoundingClientRect()]))
        : new Map();
      cancelMotion();
      pendingRender = true;
    };

    const animateCard = (card, keyframes, token) => {
      card.setAttribute('data-dap-gallery-moving', '');
      const animation = card.animate(keyframes, {
        duration: 460,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'both'
      });
      animations.set(card, animation);
      animation.finished.then(() => {
        if (token !== generation || animations.get(card) !== animation) return;
        animations.delete(card);
        card.removeAttribute('data-dap-gallery-moving');
        animation.cancel();
      }).catch(() => { /* A newer action owns the next transition. */ });
    };

    const afterRender = () => {
      assignPositions();
      const canAnimate = hasRendered && pendingRender && !reducedMotion.matches && typeof grid.animate === 'function';
      hasRendered = true;
      pendingRender = false;
      if (!canAnimate) {
        before.clear();
        return;
      }

      const first = before;
      before = new Map();
      const token = generation;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (token !== generation || reducedMotion.matches || !section.isConnected) return;
        visibleCards().forEach((card) => {
          const last = card.getBoundingClientRect();
          const prior = first.get(card);
          if (last.width === 0 || last.height === 0 || (!nearViewport(last) && (!prior || !nearViewport(prior)))) return;

          if (!prior) {
            animateCard(card, [{ transform: 'translateY(18px)' }, { transform: 'none' }], token);
            return;
          }

          const x = prior.left - last.left;
          const y = prior.top - last.top;
          // Caption wrapping can change the card's height independently of its
          // artwork. A uniform scale preserves the art's exact proportions.
          const scale = prior.width / last.width;
          if (Math.abs(x) < 1 && Math.abs(y) < 1 && Math.abs(scale - 1) < .005) return;

          animateCard(card, [
            { transform: `translate(${x}px, ${y}px) scale(${scale})` },
            { transform: 'none' }
          ], token);
        });
      });
    };


    const instance = {
      beforeRender,
      afterRender,
      cancelMotion,
      destroy() {
        cancelMotion();
        instances.delete(section);
      }
    };
    instances.set(section, instance);
    assignPositions();
    return instance;
  };

  const initWithin = (root) => {
    if (root.matches?.(sectionSelector)) init(root);
    root.querySelectorAll?.(sectionSelector).forEach(init);
  };

  document.addEventListener('dap:collection:will-render', (event) => init(event.target)?.beforeRender());
  document.addEventListener('dap:collection:rendered', (event) => init(event.target)?.afterRender());
  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, section) => {
      if (event.target === section || event.target.contains?.(section)) instance.destroy();
    });
  });
  window.addEventListener('resize', () => instances.forEach((instance) => instance.cancelMotion()), { passive: true });
  reducedMotion.addEventListener?.('change', () => instances.forEach((instance) => instance.cancelMotion()));

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  else initWithin(document);
})();
